import React, { useState } from "react";
import { Button } from "../../../components/UI/button";
import { LoaderVectors } from "../../../constants/icons";
import { defaultLanguage } from "../../../constants/languages";
import { isIOS } from "react-device-detect";
import { useSystem } from "../../../hooks/use-system";
import callAPI from "../../../api";

export default function ResultStep({
  config,
  language,
  shortCode,
  keyword,
  setStep,
  isLandscape,
}) {
  const [loading, setLoading] = useState(false);
  const system = useSystem();
  const [testSuccess, setTestSuccess] = useState(false);
  
  // These two functions are taken directly from Flow/index.js
  const listentoSubscription = (ref_id, session_id) => {
    callAPI({
      token: ref_id,
      session_id: session_id
    }, 'listenSubscription').then((data) => {
      if (data.status === 'success') {
        window.location.href = data.payload.redirect_link
      } else {
        setTimeout(() => {
          listentoSubscription(ref_id, session_id)
        }, 5000);
      }
    });
  }

  const prepareAndInitSMS = (short_code, keyword) => {
    const smsCommand = isIOS ? `sms://open?addresses=${short_code};?&body=${keyword}` : `sms:${short_code};?&body=${keyword}`;
    window.location.assign(smsCommand)
  }
  
  // SMS preparation logic
  const handleSendSMS = () => {
    setLoading(true);
    
    // Check if we're in testing mode
    if (config.is_testing) {
      // Special handling for CLICK_2_SMS_REDIRECT in testing mode
      if (config.flow === 'CLICK_2_SMS_REDIRECT') {
        // Show loading for a bit longer to simulate API call and SMS preparation
        setTimeout(() => {
          setLoading(false);
          setTestSuccess(true);
          
          // Simulate auto-redirect after successful SMS sending
          setTimeout(() => {
            console.log('Test mode: SMS app would automatically open and redirect in production mode');
            // After SMS is sent successfully, advance to success page
            setStep(2);
          }, 2000);
        }, 2500);
        return;
      }
      
      // Regular testing flow for other modes
      setTimeout(() => {
        setLoading(false);
        setTestSuccess(true);
        
        // If needed, add a step to move to a success page after testing
        setTimeout(() => {
          if (config.flow === 'PIN_FLOW') {
            setStep(2); // Move to success page for PIN_FLOW
          } else if (config.flow === 'CLICK_2_SMS_REDIRECT') {
            setStep(2); // Move to success page for CLICK_2_SMS_REDIRECT
          } else {
            // For other flows, you can define what should happen next
            console.log('SMS sent successfully in test mode');
            // Uncomment if you want to go to next step
            // setStep((prev) => prev + 1);
          }
        }, 1500);
      }, 2000);
      return;
    }
    
    // Prepare SMS text with suffix if available
    const smsSuffix = localStorage.getItem('nj_smsSuffix');
    let smsText = keyword ? keyword : config.keyword;
    if (smsSuffix) {
      smsText += ` ${smsSuffix}`;
    }
    
    // Use short code from props or config
    let smsShortCode = shortCode ? shortCode : config.short_code;
    
    // Specific handling for CLICK_2_SMS_REDIRECT
    if (config.flow === 'CLICK_2_SMS_REDIRECT') {
      callAPI({
        phone_number: localStorage.getItem('phone'),
        country_code: config.country_code,
        path: window.location.pathname,
        carrier_sub_code: null,
        session_id: localStorage.getItem('nj_sessionId'),
        token: localStorage.getItem('nj_token'),
        supportStorage: true,
        web_click_id: system.webClickId,
        config_id: system.config.public_id,
        qs: window.location.search,
        language: system.language,
        af_token: config.antifraud_token,
      }, 'initSubscription').then((data) => {
        if (data.status === 'success') {
          const curKeyword = data.payload.keyword;
          prepareAndInitSMS(data.payload.short_code, curKeyword);
          listentoSubscription(data.payload.ref_id, data.payload.session_id);
        }
        setLoading(false);
      });
    } else {
      // Regular SMS handling for other flows
      const smsCommand = isIOS 
        ? `sms://open?addresses=${smsShortCode};?&body=${smsText}` 
        : `sms:${smsShortCode};?&body=${smsText}`;
      window.location.assign(smsCommand);
      setLoading(false);
    }
  };

  return (
    <div className={`w-full ${isLandscape ? 'max-w-sm' : 'max-w-md'} flex flex-col justify-center items-center gap-3 ${isLandscape ? 'mt-1' : 'mt-4'}`}>
      <div className={`w-full ${isLandscape ? 'max-w-[300px]' : 'max-w-[275px] sm:max-w-xs'}`}>
        {testSuccess ? (
          <div className={`${isLandscape ? 'mb-3' : 'mb-6'} bg-green-100 p-4 rounded-lg border border-green-300`}>
            <p className={`text-green-700 text-center font-medium ${isLandscape ? 'text-xs' : ''}`}>
              Test successful! SMS would be sent in production mode.
            </p>
          </div>
        ) : (
          <>
            <div className={`${isLandscape ? 'mb-3' : 'mb-6'}`}>
              <p 
                className={`${isLandscape ? 'text-[10px]' : 'text-sm'} text-center`}
                style={{
                  color: config.text_color || "#000",
                }}
                dangerouslySetInnerHTML={{ 
                  __html: config.texts?.[language || defaultLanguage]?.[1]?.description || ""
                }}>
              </p>
            </div>
            
            <div className={`text-center ${isLandscape ? 'mb-2' : 'mb-4'}`}>
              <p className={`font-semibold ${isLandscape ? 'text-xs' : ''}`}>Send <span className="font-bold">{keyword || config.keyword}</span> to</p>
              <p className={`font-bold ${isLandscape ? 'text-base' : 'text-lg'}`}>{shortCode || config.short_code}</p>
            </div>
          </>
        )}
      </div>

      <Button
        className={`flex flex-row gap-2 ${isLandscape ? 'min-h-[40px] text-sm' : 'min-h-[48px] sm:min-h-[56px] text-base sm:text-lg'} px-5 py-3 w-full ${
          isLandscape ? 'max-w-[300px]' : 'max-w-[275px] sm:max-w-xs'
        } ${loading ? "cursor-not-allowed" : "cursor-pointer"} text-white bg-blue-600 hover:bg-blue-700 font-semibold rounded-lg ${
          config.animation_enabled && !testSuccess ? "animate-[pulse_1.5s_ease-in-out_infinite]" : ""
        }`}
        style={{
          backgroundColor: testSuccess ? "#47C98D" : (config.button_primary_color || "#2563eb"),
          color: config.button_text_color || "#fff",
          fontSize: config.button_font_size,
        }}
        onClick={handleSendSMS}
        disabled={loading || testSuccess || (config.flow === 'CLICK_2_SMS' && !config.short_code)}
      >
        {loading ? (
          <>
            <span className="animate-spin flex-shrink-0">
              <LoaderVectors />
            </span>
            <div className="flex flex-row justify-end items-center">
              <p className={`m-0 ${isLandscape ? 'text-xs' : ''}`}>
                {config.is_testing ? "Simulating SMS send" : "Opening SMS app"}
              </p>
              <span className="animate-bounce">.</span>
              <span className="animate-bounce delay-200">.</span>
              <span className="animate-bounce delay-300">.</span>
            </div>
          </>
        ) : testSuccess ? (
          "SMS Sent Successfully"
        ) : (
          config.texts?.[language || defaultLanguage]?.[1]?.button_text || "SEND SMS"
        )}
      </Button>
      
      <div className={`${isLandscape ? 'text-[10px] mt-1' : 'text-xs mt-2'} text-center`}>
        {config.texts?.[language || defaultLanguage]?.[1]?.button_caption || ""}
      </div>
      
      {config.is_testing && !testSuccess && (
        <div className={`${isLandscape ? 'mt-1 text-[10px]' : 'mt-4 text-xs'} text-center text-gray-500`}>
          Testing Mode: Click button to simulate SMS sending without opening Messages app
        </div>
      )}
    </div>
  );
} 